import { v7 as uuidv7 } from 'uuid'
import type { EntityLinkDocument } from '@/lib/db/rxdb/hooks/entities/entity-links'
import { extractEntityLinksFromContent } from '@/components/editor/entityLinkUtils'
import { LINK_TYPES } from '@/lib/db/rxdb/schemas/entityLink'

interface ReconcileParams {
  sourceEntityType: string
  sourceEntityId: string
  sourceEntityName: string
  envId: string
  userId: string
  content: string
  existingInlineLinks: Array<EntityLinkDocument>
  createEntityLink: (data: any) => Promise<any>
  deleteEntityLink: (id: string) => Promise<void>
}

/**
 * Reconcile inline entity links: compare what's in the content with
 * what's stored in entitylinks (linkType=0), then create/delete as needed.
 *
 * Only affects inline links (linkType=0). Explicit links (linkType=1)
 * are never touched.
 */
export async function reconcileInlineEntityLinks({
  sourceEntityType,
  sourceEntityId,
  sourceEntityName,
  envId,
  userId,
  content,
  existingInlineLinks,
  createEntityLink,
  deleteEntityLink,
}: ReconcileParams) {
  const extractedLinks = extractEntityLinksFromContent(content)

  // Build a set of currently referenced entity IDs
  const currentTargetKeys = new Set(
    extractedLinks.map((l) => `${l.entityId}:${l.entityType}`),
  )

  // Build a set of existing inline link target keys
  const existingTargetKeys = new Map<string, string>() // key -> linkId
  for (const link of existingInlineLinks) {
    const key = `${link.targetEntityId}:${link.targetEntityType}`
    existingTargetKeys.set(key, link.id)
  }

  const now = Date.now()

  // Create new inline links for newly referenced entities
  const toCreate = extractedLinks.filter(
    (l) => !existingTargetKeys.has(`${l.entityId}:${l.entityType}`),
  )

  // Soft-delete links for entities no longer referenced in content
  const toDelete: Array<string> = []
  for (const [key, linkId] of existingTargetKeys) {
    if (!currentTargetKeys.has(key)) {
      toDelete.push(linkId)
    }
  }

  // Execute creates and deletes
  await Promise.all([
    ...toCreate.map((link) =>
      createEntityLink({
        id: uuidv7(),
        envId,
        sourceEntityType,
        sourceEntityId,
        targetEntityType: link.entityType,
        targetEntityId: link.entityId,
        linkType: LINK_TYPES.INLINE,
        isDeleted: false,
        sourceEntityName,
        sourceEntityIcon: null,
        targetEntityName: link.label,
        targetEntityIcon: null,
        createdOn: now,
        createdBy: userId,
        updatedOn: now,
        updatedBy: userId,
      }),
    ),
    ...toDelete.map((id) => deleteEntityLink(id)),
  ])
}
